import { Color } from '@tiptap/extension-color'
import Highlight from '@tiptap/extension-highlight'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import { Table } from '@tiptap/extension-table'
import { TableCell } from '@tiptap/extension-table-cell'
import { TableHeader } from '@tiptap/extension-table-header'
import { TableRow } from '@tiptap/extension-table-row'
import { TaskItem } from '@tiptap/extension-task-item'
import TaskList from '@tiptap/extension-task-list'
import { TextStyle } from '@tiptap/extension-text-style'
import Typography from '@tiptap/extension-typography'
import Underline from '@tiptap/extension-underline'
import type { Extensions } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { useMemo } from 'react'

import { createMentionExtension } from '../extensions/mention/mention.extension'
import { tiptapHtmlAttributes } from '../markdown-editor.config'
import type { MentionItem } from '../markdown-editor.interface'

export interface UseMarkdownEditorExtensionsProps {
  placeholder: string
  mentionList: MentionItem[]
  mentionTrigger: string
  onMentionSearch?: (query: string) => MentionItem[] | Promise<MentionItem[]>
  onMentionSelect?: (item: MentionItem) => void
}

/**
 * Creates the base TipTap extensions without mentions
 */
const createBaseExtensions = (placeholder: string): Extensions => [
  StarterKit.configure({
    codeBlock: { HTMLAttributes: tiptapHtmlAttributes.codeBlock },
    blockquote: { HTMLAttributes: tiptapHtmlAttributes.blockquote },
    heading: { levels: [1, 2, 3, 4] },
  }),
  Underline,
  Link.configure({
    openOnClick: false,
    HTMLAttributes: tiptapHtmlAttributes.link,
  }),
  Placeholder.configure({
    placeholder,
    emptyEditorClass: 'is-editor-empty',
  }),
  Typography,
  TaskList.configure({ HTMLAttributes: tiptapHtmlAttributes.taskList }),
  TaskItem.configure({
    nested: true,
    HTMLAttributes: tiptapHtmlAttributes.taskItem,
  }),
  Table.configure({
    resizable: true,
    HTMLAttributes: tiptapHtmlAttributes.table,
  }),
  TableRow,
  TableHeader.configure({ HTMLAttributes: tiptapHtmlAttributes.tableHeader }),
  TableCell.configure({ HTMLAttributes: tiptapHtmlAttributes.tableCell }),
  TextStyle,
  Color,
  Highlight.configure({
    multicolor: false,
    HTMLAttributes: tiptapHtmlAttributes.highlight,
  }),
]

/**
 * Hook that creates and memoizes TipTap editor extensions
 */
export const useMarkdownEditorExtensions = ({
  placeholder,
  mentionList,
  mentionTrigger,
  onMentionSearch,
  onMentionSelect,
}: UseMarkdownEditorExtensionsProps): Extensions =>
  useMemo((): Extensions => {
    const baseExtensions = createBaseExtensions(placeholder)

    if (mentionList.length === 0 && !onMentionSearch) {
      return baseExtensions
    }

    return [
      ...baseExtensions,
      createMentionExtension({
        items: mentionList,
        trigger: mentionTrigger,
        onSearch: onMentionSearch,
        onSelect: onMentionSelect,
      }),
    ]
  }, [mentionList, mentionTrigger, onMentionSearch, onMentionSelect, placeholder])
